import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO, IMG } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" onClick={go} variant="outline" className="w-full rounded-full border-slate-300 font-semibold" data-testid="google-login-btn">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" /> Continue with Google
    </Button>
  );
}

export function AuthShell({ children, title, sub }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy">
      <div className="relative hidden lg:block">
        <img src={IMG.heroPortal} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/30" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <p className="font-display font-black text-4xl text-white leading-tight">THE ROCK<br />IS CALLING.</p>
          <p className="text-turquoise mt-2 font-semibold">After school, the adventure begins.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-limestone">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8"><img src={LOGO} className="h-12 w-12 object-contain" alt="logo" /><span className="font-display font-extrabold text-navy">ADVENTURE ROCK</span></Link>
          <h1 className="font-display font-extrabold text-3xl text-navy">{title}</h1>
          <p className="text-slate-500 mt-1 mb-6">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      nav(["admin", "manager"].includes(data.role) ? "/admin" : data.role === "driver" ? "/driver" : data.role === "watcher" ? "/watcher" : data.role === "staff" ? "/staff" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Parent Login" sub="Access your Mission Control dashboard.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="rounded-xl" /></div>
        <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="rounded-xl" /></div>
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-turquoise hover:underline" data-testid="forgot-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="w-full bg-navy hover:bg-navy/90 rounded-full font-bold" data-testid="login-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}</Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">OR</span><div className="h-px bg-slate-200 flex-1" /></div>
      <GoogleButton />
      <p className="text-center text-sm text-slate-500 mt-6">New explorer? <Link to="/register" className="text-turquoise font-semibold hover:underline">Pre-register</Link></p>
    </AuthShell>
  );
}
